import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { cn, formatCurrency } from "@/lib/utils";
import { useLazyGetDeviceAvailabilityQuery } from "@/redux/services/order-portal";

export const DeviceVariantPickerModal = ({
  customerType,
  deviceName,
  variants,
  onChange,
  className,
}: {
  customerType: CustomerType;
  deviceName: string;
  variants: DeviceVariant[];
  onChange: (value: {
    id: string;
    sku: string;
    name: string;
    color: string;
    price: number;
  }) => void;
  className?: string;
}) => {
  const { t } = useTranslation();
  const ASSET_URL = import.meta.env.VITE_ASSET_URL;

  const [selectedVariant, setSelectedVariant] = useState<DeviceVariant>();

  const [getDeviceAvailability, { currentData: deviceAvailability }] =
    useLazyGetDeviceAvailabilityQuery();

  function getSKUCodes(variants: DeviceVariant[]) {
    return variants.flatMap((variant) => variant.sku_code);
  }

  function getColourOptions(variants: DeviceVariant[]) {
    return variants.flatMap((variant) =>
      variant.details.attribute.Color ? [variant.details.attribute.Color] : []
    );
  }

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open) {
          setSelectedVariant(undefined);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          className={cn("size-full p-4", className)}
          onClick={() => {
            setSelectedVariant(variants[0]);
            getDeviceAvailability({
              customer_type: customerType,
              sku_code: getSKUCodes(variants),
              warehouse_id: 5,
              store_id: 10,
            });
          }}
          size="card"
          variant="outline"
        >
          {variants[0]?.images?.[0]?.image_file ? (
            // biome-ignore lint/correctness/useImageSize: Automatic sizing
            <img
              alt={deviceName}
              className="w-full object-cover"
              src={`${ASSET_URL}/${variants[0].images[0].image_file}`}
            />
          ) : (
            // biome-ignore lint/correctness/useImageSize: Automatic sizing
            <img
              alt={deviceName}
              className="w-full object-cover"
              src={"/images/placeholder.webp"}
            />
          )}
          <div className="space-y-2 text-left">
            <p className="font-semibold text-primary">{deviceName}</p>
            <p className="text-muted-foreground">
              {variants[0]?.price_amount
                ? formatCurrency(variants[0].price_amount)
                : ""}
            </p>
          </div>
        </Button>
      </DialogTrigger>
      <DialogContent className="pb-24 sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{t("device-variant-picker-modal.title")}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("device-variant-picker-modal.description")}
          </DialogDescription>
        </DialogHeader>
        <Separator />
        <div className="flex flex-col gap-6 sm:flex-row">
          <Carousel className="mx-12 flex w-full max-w-48 items-center justify-center">
            <CarouselContent>
              {selectedVariant?.images ? (
                selectedVariant?.images.map((image, index) => (
                  <CarouselItem key={`${deviceName}-${index + 1}`}>
                    <div className="flex items-center justify-center">
                      {/** biome-ignore lint/correctness/useImageSize: Automatic sizing */}
                      <img
                        alt={deviceName}
                        className="w-full object-cover"
                        src={`${ASSET_URL}/${image.image_file}`}
                      />
                    </div>
                  </CarouselItem>
                ))
              ) : (
                <CarouselItem>
                  <div className="flex items-center justify-center">
                    {/** biome-ignore lint/correctness/useImageSize: Automatic sizing */}
                    <img
                      alt={deviceName}
                      className="w-full object-cover"
                      src={"/images/placeholder.webp"}
                    />
                  </div>
                </CarouselItem>
              )}
            </CarouselContent>
            <CarouselPrevious className="border-0" />
            <CarouselNext className="border-0" />
          </Carousel>

          <div className="space-y-4">
            <p className="font-semibold text-xl">
              {selectedVariant?.bundle_name}
            </p>
            <div className="space-y-1">
              <p>{t("labels.price")}:</p>
              <p className="font-medium text-primary">
                {formatCurrency(selectedVariant?.price_amount ?? 0)}
              </p>
            </div>
            {getColourOptions(variants).length > 0 && (
              <div className="space-y-1">
                <p>{t("labels.colour")}:</p>
                <RadioGroup
                  className="flex flex-wrap gap-2"
                  onValueChange={(value) => {
                    setSelectedVariant(
                      variants.find(
                        (variant) => variant.details.attribute.Color === value
                      )
                    );
                  }}
                  value={selectedVariant?.details.attribute.Color}
                >
                  {getColourOptions(variants).map((colour) => (
                    // biome-ignore lint/a11y/noLabelWithoutControl: Label is the input for the radio group
                    <label
                      className="relative flex cursor-pointer flex-col items-center gap-3 rounded-full border border-input p-2 text-center uppercase shadow-black/5 shadow-sm outline-offset-2 transition-colors has-data-disabled:cursor-not-allowed has-data-[state=checked]:border-ring has-data-[state=checked]:text-primary has-data-disabled:opacity-50 has-focus-visible:outline has-focus-visible:outline-ring/70"
                      key={colour}
                    >
                      <RadioGroupItem
                        className="sr-only after:absolute after:inset-0"
                        id={colour}
                        value={colour}
                      />
                      <p className="font-medium text-sm leading-none">
                        {colour}
                      </p>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            )}
            <div className="space-y-1">
              <p>{t("labels.description")}:</p>
              <p>{selectedVariant?.item_description}</p>
            </div>

            <DialogClose asChild>
              <Button
                disabled={
                  deviceAvailability?.[selectedVariant?.sku_code[0] ?? ""] === 0
                }
                onClick={() => {
                  if (
                    deviceAvailability?.[selectedVariant?.sku_code[0] ?? ""] ===
                    0
                  ) {
                    return;
                  }

                  if (!selectedVariant) {
                    return;
                  }

                  onChange({
                    id: selectedVariant.bundle_id.toString(),
                    sku: selectedVariant.sku_code[0],
                    name: selectedVariant.item_name,
                    color: selectedVariant.details.attribute.Color,
                    price: selectedVariant.price_amount,
                  });
                }}
              >
                {deviceAvailability?.[selectedVariant?.sku_code[0] ?? ""] === 0
                  ? t("labels.sold-out")
                  : t("labels.choose")}
              </Button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
